import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Navbar } from '~/components/nav/Navbar';
import { Menu } from '~/components/ui/Menu';
import { useOverflowNavigation } from '~/hooks/useOverflowNavigation';

export type NavItemsBarProps = {
  items: string[];
  activeItem?: string;
  onSelect: (value: string) => void;
  labelFor: (value: string) => string;
  moreLabel?: string;
  rightSlot?: React.ReactNode;
  /** Far-right content (e.g., add button) pinned to the edge */
  farRightSlot?: React.ReactNode;
  /** Whether the config panel is open; used for rounded right-side styling */
  isPanelOpen?: boolean;
  recomputeKey?: string | number | boolean;
};

export const NavItemsBar = ({
  items,
  activeItem,
  onSelect,
  labelFor,
  moreLabel = 'More',
  rightSlot,
  farRightSlot,
  isPanelOpen,
  recomputeKey,
}: NavItemsBarProps) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const itemsRegionRef = useRef<HTMLDivElement | null>(null);
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);
  const rightRegionRef = useRef<HTMLDivElement | null>(null);
  const farRightRegionRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  // Nonce forces the measurement effect to rerun inside useOverflowNavigation
  const [recomputeNonce, setRecomputeNonce] = useState(0);
  const [reservedEndWidth, setReservedEndWidth] = useState(0);

  // Remeasure when the right/far-right regions change size (e.g., opens/closes)
  useEffect(() => {
    const rightEl = rightRegionRef.current;
    const farEl = farRightRegionRef.current;
    const onResize = () => setRecomputeNonce((n) => n + 1);

    const recomputeReserved = () => {
      const rw = rightEl?.getBoundingClientRect().width ?? 0;
      const fw = farEl?.getBoundingClientRect().width ?? 0;
      const gap = rw > 0 ? 8 : 0; // tiny spacing between tabs and config
      setReservedEndWidth(rw + gap + fw);
    };

    recomputeReserved();

    const observers: ResizeObserver[] = [];
    if (rightEl) {
      const ro = new ResizeObserver(() => {
        recomputeReserved();
        onResize();
      });
      ro.observe(rightEl);
      observers.push(ro);
    }
    if (farEl) {
      const ro = new ResizeObserver(() => {
        recomputeReserved();
        onResize();
      });
      ro.observe(farEl);
      observers.push(ro);
    }

    const onWin = () => {
      recomputeReserved();
      onResize();
    };
    window.addEventListener('resize', onWin);
    return () => {
      observers.forEach((o) => o.disconnect());
      window.removeEventListener('resize', onWin);
    };
  }, [rightSlot, farRightSlot]);

  // Also bump nonce if parent indicates recomputeKey changed
  useEffect(() => {
    if (recomputeKey !== undefined) {
      setRecomputeNonce((n) => n + 1);
    }
  }, [recomputeKey]);

  const { visibleItems, overflowItems } = useOverflowNavigation<string>({
    items,
    getLabel: (v) => labelFor(v),
    isActive: (v) => v === activeItem,
    containerRef: rowRef,
    measureRef: measureRef,
    itemMeasureClassName:
      'inline-flex shrink-0 items-center px-5 pt-1 pb-0 lg:pb-0.5 h-16 lg:h-[65px] border-b-4 border-transparent text-copy-nav whitespace-nowrap transition-all duration-300 ease-in-out' +
      (recomputeKey !== undefined ? ` rk-${String(recomputeKey)}` : '') +
      ` rn-${recomputeNonce}`,
    moreButtonMeasureClassName:
      'inline-flex shrink-0 items-center px-5 pt-1 pb-0 lg:pb-0.5 h-16 lg:h-[65px] border-b-4 border-transparent text-copy-nav whitespace-nowrap transition-all duration-300 ease-in-out' +
      (recomputeKey !== undefined ? ` rk-${String(recomputeKey)}` : ''),
    moreLabel,
    // Config and far-right are outside measurement
    reservedEndWidthPx: reservedEndWidth,
  });

  return (
    <div className='z-50 hidden items-center bg-white lg:flex lg:border-b lg:border-outline-bright'>
      <Navbar
        hasSettingsPanel={Boolean(rightSlot)}
        className='border-b-0 lg:pl-12 xl:pl-20'
      >
        {/* Row wrapper used for containerRef; we subtract right/far-right widths */}
        <div ref={rowRef} className='flex w-full min-w-0 items-stretch'>
          {/* 1) Tabs region: ONLY measured area */}
          <div
            ref={itemsRegionRef}
            className={classNames(
              'flex min-w-0 items-stretch',
              isPanelOpen
                ? 'relative z-10 rounded-r-full bg-white pr-4 after:pointer-events-none after:absolute after:-inset-0.5 after:rounded-r-full after:border after:border-l-0 after:border-outline-bright'
                : undefined
            )}
          >
            {/* Measured items area */}
            <div
              ref={itemsContainerRef}
              className={classNames('flex min-w-0', 'mr-2')}
            >
              {visibleItems.map((t) => {
                const isActive = t === activeItem;
                return (
                  <Navbar.Item
                    key={t}
                    as='button'
                    className={classNames(
                      'border-b-4 pr-1 text-copy-nav transition-all duration-300 ease-in-out',
                      isActive
                        ? 'active font-semibold'
                        : 'border-transparent hover:border-current'
                    )}
                    onClick={() => onSelect(t)}
                  >
                    {labelFor(t)}
                  </Navbar.Item>
                );
              })}
              {overflowItems.length > 0 && (
                <div className='flex'>
                  <Menu
                    usePortal={true}
                    buttonClassName='flex items-center px-4 pt-1 pb-0 lg:pb-0.5 h-16 lg:h-[65px] border-b-4 border-transparent text-copy-nav whitespace-nowrap transition-all duration-300 ease-in-out hover:border-current'
                    buttonText={
                      <span className='inline-flex items-center gap-2'>
                        {moreLabel}
                        <FontAwesomeIcon
                          icon={faChevronDown as IconProp}
                          className='h-4 w-4'
                        />
                      </span>
                    }
                  >
                    {overflowItems.map((t) => (
                      <Menu.Item
                        key={t}
                        as='button'
                        onClick={() => onSelect(t)}
                      >
                        {labelFor(t)}
                      </Menu.Item>
                    ))}
                  </Menu>
                </div>
              )}
              {/* Spacer to convert the reserved area into real layout space */}
              <div
                aria-hidden='true'
                className='shrink-0'
                style={{
                  width: 8,
                }}
              />
            </div>
          </div>

          {/* 2) Config immediately after tabs */}
          {rightSlot ? (
            <div ref={rightRegionRef} className='flex shrink-0 items-center'>
              {rightSlot}
            </div>
          ) : null}

          {/* 3) Far-right content pinned with auto margin */}
          {farRightSlot ? (
            <div
              ref={farRightRegionRef}
              className='ml-auto flex shrink-0 items-center pl-2'
            >
              {farRightSlot}
            </div>
          ) : null}
        </div>

        <div
          ref={measureRef}
          aria-hidden='true'
          className='pointer-events-none absolute left-0 top-0 h-0 overflow-hidden opacity-0'
          style={{ visibility: 'hidden' }}
        />
      </Navbar>
    </div>
  );
};
